<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '🍽️ Apa itu Tipe Order?',
    icon: 'ri-list-check-2',
    color: 'success',
    content: `
      <p class="mb-4">
        Setiap kali kasir membuat pesanan, sistem sekarang menanyakan
        <strong>Tipe Order</strong> — apakah tamu makan di tempat, bawa pulang,
        atau pesanan lewat ojek online. Ada 3 pilihan:
      </p>
      <VRow class="mb-4">
        <VCol cols="12" md="4">
          <VCard variant="outlined">
            <VCardText class="text-center">
              <VIcon icon="ri-restaurant-line" size="32" color="primary" class="mb-2" />
              <p class="font-weight-bold mb-1">Dine-in</p>
              <p class="text-caption mb-0">Tamu makan di tempat, biasanya pakai meja</p>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="12" md="4">
          <VCard variant="outlined">
            <VCardText class="text-center">
              <VIcon icon="ri-shopping-bag-line" size="32" color="primary" class="mb-2" />
              <p class="font-weight-bold mb-1">Takeaway</p>
              <p class="text-caption mb-0">Tamu beli dan langsung bawa pulang</p>
            </VCardText>
          </VCard>
        </VCol>
        <VCol cols="12" md="4">
          <VCard variant="outlined">
            <VCardText class="text-center">
              <VIcon icon="ri-motorbike-line" size="32" color="primary" class="mb-2" />
              <p class="font-weight-bold mb-1">Delivery</p>
              <p class="text-caption mb-0">Pesanan lewat GoFood/GrabFood/dll</p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
      <p class="mb-0">
        Kenapa ini penting? Karena <strong>biaya tambahan</strong> untuk setiap
        tipe order bisa berbeda — dan halaman ini yang mengaturnya.
      </p>
    `,
  },
  {
    title: '💰 Mengapa Butuh Biaya Tambahan per Tipe Order?',
    icon: 'ri-money-dollar-circle-line',
    color: 'warning',
    content: `
      <p class="mb-3">
        Ini beberapa contoh alasan bisnis F&B biasanya mengenakan biaya
        tambahan berbeda-beda:
      </p>
      <ul class="mb-4">
        <li class="mb-2">
          <strong>Dine-in</strong> — sering dikenakan "biaya layanan" (service
          charge), misalnya 5%, karena ada biaya operasional tambahan
          (staf antar pesanan, cuci piring, dll).
        </li>
        <li class="mb-2">
          <strong>Delivery</strong> — biasanya dikenakan biaya tetap (mis. Rp3.000)
          untuk menutup sebagian potongan komisi dari platform ojek online.
        </li>
        <li class="mb-0">
          <strong>Takeaway</strong> — biasanya TIDAK dikenakan biaya tambahan
          sama sekali karena tidak ada layanan makan di tempat.
        </li>
      </ul>
      <VAlert type="info" variant="tonal">
        Semua ini <strong>opsional</strong>. Kalau toko Anda tidak mau kenakan
        biaya tambahan sama sekali, cukup biarkan semua tipe order dalam
        keadaan nonaktif (default sistem).
      </VAlert>
    `,
  },
  {
    title: '⚙️ Cara Mengatur Biaya per Tipe Order',
    icon: 'ri-settings-3-line',
    color: 'primary',
    content: `
      <ol class="mb-4">
        <li class="mb-2">
          Buka menu <strong>Ekstensi F&amp;B → Tipe Order &amp; Delivery →
          Biaya per Tipe Order</strong>
        </li>
        <li class="mb-2">
          Anda akan melihat 3 baris pengaturan: Dine-in, Takeaway, Delivery
        </li>
        <li class="mb-2">
          Untuk tipe order yang mau dikenakan biaya, nyalakan sakelar
          <strong>"Aktif"</strong> di baris tersebut
        </li>
        <li class="mb-2">Isi 2 field berikut:</li>
      </ol>

      <table class="userguide-table mb-4">
        <thead>
          <tr><th>Field</th><th>Penjelasan</th><th>Contoh</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Jenis Biaya</strong></td>
            <td>
              <em>Persentase</em> = biaya dihitung dari total belanja.
              <em>Nominal Tetap</em> = biaya rupiah tetap berapa pun total belanjanya.
            </td>
            <td>Persentase, atau Nominal Tetap</td>
          </tr>
          <tr>
            <td><strong>Nilai</strong></td>
            <td>Angka biayanya — persen (%) kalau pilih Persentase, atau Rupiah kalau pilih Nominal Tetap</td>
            <td>5 (untuk 5%), atau 3000 (untuk Rp3.000)</td>
          </tr>
        </tbody>
      </table>

      <ol start="5" class="mb-4">
        <li class="mb-0">Klik <strong>"Simpan Konfigurasi"</strong> di bagian bawah halaman</li>
      </ol>

      <VAlert type="success" variant="tonal">
        💡 Contoh Nyata: Anda set <strong>Dine-in = Persentase 5%</strong>.
        Kalau ada pesanan dine-in dengan subtotal Rp100.000, sistem otomatis
        menambahkan Rp5.000 biaya layanan, jadi totalnya Rp105.000.
      </VAlert>
    `,
  },
  {
    title: '🧾 Bagaimana Biaya Ini Muncul di Kasir?',
    icon: 'ri-shopping-cart-2-line',
    color: 'info',
    content: `
      <p class="mb-4">
        Begitu Anda simpan konfigurasi biaya, halaman kasir (POS) langsung
        menyesuaikan otomatis — kasir tidak perlu hitung manual.
      </p>
      <ol class="mb-4">
        <li class="mb-2">
          Di halaman kasir, kasir memilih salah satu dari 3 tombol tipe order:
          <strong>Dine-in / Takeaway / Delivery</strong>
        </li>
        <li class="mb-2">
          Kalau tipe order itu punya biaya aktif, akan langsung muncul baris
          tambahan di ringkasan belanja, contoh:
          <em>"Biaya Dine-in (5%) — Rp5.000"</em>
        </li>
        <li class="mb-0">
          Total yang diminta ke pelanggan sudah termasuk biaya ini
        </li>
      </ol>
      <VAlert type="warning" variant="tonal">
        ⚠️ Angka yang muncul di layar kasir adalah <strong>perkiraan</strong>.
        Sistem tetap menghitung ulang angka final saat transaksi benar-benar
        disimpan, untuk memastikan akurasi 100%.
      </VAlert>
    `,
  },
  {
    title: '🛵 Kalau Pilih Delivery, Wajib Pilih Platform',
    icon: 'ri-motorbike-line',
    color: 'error',
    content: `
      <p class="mb-4">
        Khusus tipe order <strong>Delivery</strong>, kasir wajib memilih dulu
        platform ojek online mana yang dipakai (GoFood/GrabFood/dll) sebelum
        bisa lanjut ke pembayaran. Tombol "Bayar" akan terkunci sampai
        platform-nya dipilih.
      </p>
      <p class="mb-0">
        Cara menambah daftar platform yang bisa dipilih kasir, lihat panduan
        <strong>Platform Delivery</strong> di halaman terkait bawah ini.
      </p>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide/fb-modern">
      <VBtn variant="text" prepend-icon="ri-arrow-left-line" class="mb-2">
        Kembali ke F&B Modern
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar color="success" variant="tonal" size="64" rounded="lg" class="me-4">
        <VIcon icon="ri-percent-line" size="36" />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">Modul 14: Tipe Order & Biaya Layanan</h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Dine-in, Takeaway, Delivery — dan biaya tambahan otomatis untuk masing-masing
        </p>
      </div>
    </div>

    <VExpansionPanels variant="accordion" multiple>
      <VExpansionPanel v-for="(section, index) in sections" :key="index">
        <VExpansionPanelTitle>
          <VIcon :icon="section.icon" :color="section.color" class="me-3" />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apakah biaya ini wajib saya aktifkan?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak wajib sama sekali. Kalau sakelar "Aktif" dimatikan untuk semua
          tipe order, tidak akan ada biaya tambahan apa pun — sistem berjalan
          seperti biasa tanpa surcharge.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Bisa tidak biaya dine-in dihitung dari total setelah diskon, bukan dari subtotal?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Perhitungan detail seperti ini ditentukan oleh sistem di belakang
          layar. Kalau Anda menemukan angka yang menurut Anda tidak sesuai,
          hubungi tim support untuk pengecekan lebih lanjut.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Kalau saya ubah persentase biaya, apakah transaksi lama ikut berubah?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Tidak. Perubahan konfigurasi hanya berlaku untuk transaksi baru
          setelah Anda simpan. Transaksi yang sudah tercatat sebelumnya
          tetap memakai angka yang berlaku saat transaksi itu dibuat.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">🔗 Halaman Terkait</h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Platform Delivery', sub: 'Daftarkan GoFood, GrabFood, dll', icon: 'ri-motorbike-line', color: 'warning', to: { name: 'userguide-fb-modern-delivery-platforms' } },
          { title: 'Meja & Denah Meja', sub: 'Konteks meja untuk pesanan dine-in', icon: 'ri-table-2', color: 'primary', to: { name: 'userguide-fb-modern-tables' } },
          { title: 'Sistem Antrian', sub: 'Nomor antrian otomatis per tipe order — diatur dari halaman ini juga', icon: 'ri-hashtag', color: 'success', to: { name: 'userguide-operasional-sistem-antrian' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink :to="link.to" class="text-decoration-none">
          <VCard variant="outlined" hover>
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                <VIcon :icon="link.icon" size="22" />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.userguide-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.userguide-table th,
.userguide-table td {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.userguide-table th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-weight: 600;
}
</style>
